import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import { Play, Pause, SkipBack, SkipForward, Square, Download, MousePointer2 } from "lucide-react";

import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { DeviceCombobox } from "../components/common/DeviceCombobox";
import { Calendar } from "../components/Calendar/Calendar";
import { Timeline } from "../components/Timeline/Timeline";
import { PlaybackCell, type PlaybackCellHandle } from "../components/Playback/PlaybackCell";
import { PlaybackChannelList } from "../components/Playback/PlaybackChannelList";
import { DownloadRangeModal } from "./playback/DownloadRangeModal";
import { useDevices } from "../hooks/useDevices";
import { useDevicesStore } from "../store/devicesStore";
import { useAuthStore } from "../store/authStore";
import { hasPermission } from "../lib/permissions";
import { availableDays } from "../api/playback";
import { toLocalIso } from "../lib/playbackTime";
import { usePlaybackGridStore, PB_COUNTS, PB_LAYOUTS } from "../store/playbackGridStore";
import type { PlaybackFile } from "../types";

const SPEEDS = [0.5, 1, 2, 4, 8];

export default function PlaybackPage() {
  useDevices();
  const user = useAuthStore((s) => s.user);
  const devices = useDevicesStore((s) => s.devices);
  const channelsByDevice = useDevicesStore((s) => s.channelsByDevice);
  const canDownload = hasPermission(user, "can_download_recordings");

  const deviceId = usePlaybackGridStore((s) => s.deviceId);
  const date = usePlaybackGridStore((s) => s.date);
  const count = usePlaybackGridStore((s) => s.count);
  const cells = usePlaybackGridStore((s) => s.cells);
  const mode = usePlaybackGridStore((s) => s.mode);
  const focusedCellId = usePlaybackGridStore((s) => s.focusedCellId);
  const setDevice = usePlaybackGridStore((s) => s.setDevice);
  const setDate = usePlaybackGridStore((s) => s.setDate);
  const setCount = usePlaybackGridStore((s) => s.setCount);
  const setMode = usePlaybackGridStore((s) => s.setMode);
  const setFocused = usePlaybackGridStore((s) => s.setFocused);
  const clearAll = usePlaybackGridStore((s) => s.clearAll);

  const selectedDevice = useMemo(
    () => devices.find((d) => d.id === deviceId) ?? null,
    [devices, deviceId],
  );

  const [seekTime, setSeekTime] = useState("00:00");
  const [rate, setRate] = useState(1);
  const [selection, setSelection] = useState<{ start: string; end: string } | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [visMonth, setVisMonth] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });

  const [filesByCell, setFilesByCell] = useState<Record<string, PlaybackFile[]>>({});
  const [cursorByCell, setCursorByCell] = useState<Record<string, string | null>>({});

  const cellRefs = useRef<Record<string, PlaybackCellHandle | null>>({});

  const assignedCellIds = useMemo(
    () => cells.filter((c) => c.channel != null).map((c) => c.cellId),
    [cells],
  );

  const focusedCell = cells.find((c) => c.cellId === focusedCellId) ?? null;
  const focusedChannel = focusedCell?.channel ?? null;
  const focusedFiles = (focusedCellId && filesByCell[focusedCellId]) || [];
  const focusedCursor = (focusedCellId && cursorByCell[focusedCellId]) || null;

  const handleFilesChange = useCallback((cid: string, f: PlaybackFile[]) => {
    setFilesByCell((prev) => (prev[cid] === f ? prev : { ...prev, [cid]: f }));
  }, []);
  const handleCursorChange = useCallback((cid: string, iso: string | null) => {
    setCursorByCell((prev) => (prev[cid] === iso ? prev : { ...prev, [cid]: iso }));
  }, []);
  const handleFocus = useCallback((cid: string) => setFocused(cid), [setFocused]);

  // Targets de la barra de transporte: sync = todas las celdas con canal;
  // independiente = solo la celda enfocada.
  const targetIds = useCallback((): string[] => {
    if (mode === "sync") return assignedCellIds;
    return focusedCellId && focusedChannel != null ? [focusedCellId] : [];
  }, [mode, assignedCellIds, focusedCellId, focusedChannel]);

  const seekTargets = (iso: string) => targetIds().forEach((id) => cellRefs.current[id]?.seek(iso));
  const pauseTargets = (p: boolean) => targetIds().forEach((id) => cellRefs.current[id]?.setPaused(p));
  const stopTargets = () => targetIds().forEach((id) => cellRefs.current[id]?.stop());

  // Cursor "maestro" para ±10s: el de la celda enfocada, o el primero disponible.
  const masterCursor = (): string | null => {
    if (focusedCursor) return focusedCursor;
    for (const id of assignedCellIds) {
      if (cursorByCell[id]) return cursorByCell[id]!;
    }
    return null;
  };

  const onPlay = () => {
    if (targetIds().length === 0) {
      toast.error(mode === "sync" ? "Arrastra al menos un canal a la grilla" : "Selecciona (clic) una camara con canal");
      return;
    }
    seekTargets(`${date}T${seekTime}:00`);
  };

  const onStep = (delta: number) => {
    if (targetIds().length === 0) return;
    const m = masterCursor();
    const base = m ? new Date(m) : new Date(`${date}T${seekTime}:00`);
    base.setSeconds(base.getSeconds() + delta);
    const ds = new Date(`${date}T00:00:00`);
    const de = new Date(`${date}T23:59:59`);
    if (base < ds) base.setTime(ds.getTime());
    if (base > de) base.setTime(de.getTime());
    seekTargets(toLocalIso(base));
  };

  // Calendario: dias con grabacion del canal ENFOCADO (aproximacion razonable;
  // las grabaciones suelen ser homogeneas entre canales del mismo equipo).
  const calQuery = useQuery({
    queryKey: ["pbDays", deviceId, focusedChannel, visMonth.y, visMonth.m],
    queryFn: async () => {
      const ms = new Date(visMonth.y, visMonth.m, 1);
      const me = new Date(visMonth.y, visMonth.m + 1, 0, 23, 59, 59);
      const res = await availableDays({
        device_id: deviceId!, channel: focusedChannel!,
        start: toLocalIso(ms), end: toLocalIso(me),
      });
      return res.days;
    },
    enabled: deviceId != null && focusedChannel != null,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
  });
  const availableDaysSet = useMemo(() => new Set(calQuery.data ?? []), [calQuery.data]);

  const onExportRecent = (minutes: number) => {
    if (!canDownload) { toast.error("Sin permiso para descargar"); return; }
    if (!focusedCursor || deviceId == null || focusedChannel == null) {
      toast.error("Reproduce/seek en la camara enfocada primero");
      return;
    }
    const endD = new Date(focusedCursor);
    const startD = new Date(endD.getTime() - minutes * 60_000);
    const ds = new Date(`${date}T00:00:00`);
    if (startD < ds) startD.setTime(ds.getTime());
    setSelection({ start: toLocalIso(startD), end: toLocalIso(endD) });
    setShowDownloadModal(true);
    setExportMenuOpen(false);
  };

  const onDownload = () => {
    if (!canDownload) { toast.error("Sin permiso para descargar"); return; }
    if (deviceId == null || focusedChannel == null || !selection) {
      toast.error("Selecciona un tramo en la barra inferior (camara enfocada)");
      return;
    }
    setShowDownloadModal(true);
  };

  const [cols, rows] = PB_LAYOUTS[count] ?? [
    Math.ceil(Math.sqrt(count)),
    Math.ceil(count / Math.ceil(Math.sqrt(count))),
  ];

  const channelNameOf = (ch: number | null): string | null => {
    if (deviceId == null || ch == null) return null;
    return (channelsByDevice[deviceId] || []).find((c) => c.channel_number === ch)?.name ?? null;
  };

  return (
    <div className="flex h-full">
      <aside className="w-52 flex-shrink-0">
        <PlaybackChannelList device={selectedDevice} />
      </aside>

      <section className="flex-1 flex flex-col min-w-0">
        {/* Barra superior */}
        <div className="flex items-center flex-wrap gap-2 bg-panel border-b border-line px-2 py-1.5 text-xs">
          <DeviceCombobox
            devices={devices}
            value={deviceId}
            onChange={(id) => setDevice(id)}
            placeholder="Buscar abonado o IP..."
          />
          {selectedDevice && (
            <span
              className={clsx("inline-block w-3 h-3 rounded-full shrink-0",
                selectedDevice.is_online ? "bg-green-500" : "bg-red-500")}
              title={selectedDevice.is_online ? "Equipo en linea" : "Equipo fuera de linea"}
            />
          )}

          {/* Modo */}
          <div className="flex items-center gap-1 border-l border-line pl-2">
            <button
              className={clsx("px-2 py-0.5 rounded", mode === "sync" ? "bg-accent text-white" : "bg-bg border border-line text-text hover:bg-line")}
              onClick={() => setMode("sync")}
              title="Todas las camaras a la misma hora; los controles mueven todas"
            >
              Sincronizado
            </button>
            <button
              className={clsx("px-2 py-0.5 rounded", mode === "independent" ? "bg-accent text-white" : "bg-bg border border-line text-text hover:bg-line")}
              onClick={() => setMode("independent")}
              title="Cada camara con su propia hora; los controles mueven la camara enfocada"
            >
              Independiente
            </button>
          </div>

          {/* Cantidad de celdas */}
          <div className="flex items-center gap-1 border-l border-line pl-2">
            {PB_COUNTS.map((n) => (
              <button
                key={n}
                className={clsx("px-2 py-0.5 rounded", count === n ? "bg-accent text-white" : "bg-bg border border-line text-text hover:bg-line")}
                onClick={() => setCount(n)}
                title={`${n} camara${n > 1 ? "s" : ""}`}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Fecha + hora + reproducir */}
          <div className="flex items-center gap-1 border-l border-line pl-2">
            <Calendar
              value={date}
              onChange={setDate}
              availableDays={availableDaysSet}
              onMonthChange={(y, m) => setVisMonth({ y, m })}
              loading={!!deviceId && focusedChannel != null && calQuery.isFetching}
            />
            <Input
              type="time"
              value={seekTime}
              onChange={(e) => setSeekTime(e.target.value)}
              className="w-auto"
              title="Hora de inicio al apretar Reproducir"
            />
          </div>

          {/* Transporte */}
          <div className="flex items-center gap-1 border-l border-line pl-2">
            <Button variant="secondary" size="sm" onClick={() => onStep(-10)} disabled={assignedCellIds.length === 0} title="Retroceder 10s">
              <SkipBack size={12} />
            </Button>
            <Button variant="primary" size="sm" onClick={onPlay} disabled={assignedCellIds.length === 0} title="Reproducir desde la hora elegida">
              <Play size={12} /> Reproducir
            </Button>
            <Button variant="secondary" size="sm" onClick={() => pauseTargets(true)} disabled={assignedCellIds.length === 0} title="Pausa">
              <Pause size={12} />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => pauseTargets(false)} disabled={assignedCellIds.length === 0} title="Reanudar">
              <Play size={12} />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onStep(10)} disabled={assignedCellIds.length === 0} title="Avanzar 10s">
              <SkipForward size={12} />
            </Button>
            <Button variant="danger" size="sm" onClick={stopTargets} disabled={assignedCellIds.length === 0} title="Detener">
              <Square size={12} />
            </Button>
          </div>

          {/* Velocidad (global). 2x/4x/8x quedan deshabilitados a proposito: el DVR
              entrega la grabacion por RTSP (/cam/playback) a TIEMPO REAL (1x). Acelerar
              solo el <video> (playbackRate>1) vacia el buffer mas rapido de lo que llega
              y se congela. Medido en prod (equipo 16): playback RTSP ~0.91x vs loadfile
              ~19x. Solo 0.5x y 1x son reproducibles sobre el stream en tiempo real. */}
          <div className="flex items-center gap-1 border-l border-line pl-2">
            {SPEEDS.map((r) => {
              const unsupported = r > 1;
              return (
                <button
                  key={r}
                  onClick={() => { if (!unsupported) setRate(r); }}
                  disabled={unsupported}
                  className={clsx("px-2 py-0.5 rounded text-[11px]",
                    rate === r ? "bg-accent text-white" : "bg-bg border border-line text-text hover:bg-line",
                    unsupported && "opacity-40 cursor-not-allowed")}
                  title={unsupported
                    ? "Velocidad rapida no disponible: el DVR entrega la grabacion a tiempo real (1x) por RTSP; acelerar congela el video. Solo 0.5x y 1x."
                    : `Velocidad ${r}x`}
                >
                  {r}x
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearAll} disabled={assignedCellIds.length === 0} title="Vaciar la grilla">
              Limpiar
            </Button>
            {canDownload && (
              <>
                <div className="relative">
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => setExportMenuOpen((o) => !o)}
                    disabled={!focusedCursor}
                    title="Descargar los ultimos minutos de la camara enfocada"
                  >
                    <Download size={12} /> Exportar reciente
                  </Button>
                  {exportMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-panel border border-line rounded shadow-lg text-xs py-1 min-w-[160px]">
                      {[5, 10, 30].map((m) => (
                        <button key={m} className="w-full text-left px-3 py-1 hover:bg-line" onClick={() => onExportRecent(m)}>
                          Ultimos {m} minutos
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant={selectMode ? "primary" : "secondary"} size="sm"
                  onClick={() => setSelectMode((s) => !s)}
                  title="Activa y arrastra en la barra inferior para seleccionar un tramo (camara enfocada)"
                >
                  <MousePointer2 size={12} /> {selectMode ? "Seleccionando..." : "Seleccionar tramo"}
                </Button>
                <Button variant="secondary" size="sm" onClick={onDownload} disabled={!selection}>
                  <Download size={12} /> Descargar tramo
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Grilla de camaras */}
        <div className="flex-1 min-h-0 overflow-hidden bg-bg p-1">
          <div
            className="w-full h-full grid gap-0.5"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            }}
          >
            {cells.map((c) => (
              <PlaybackCell
                key={c.cellId}
                ref={(h) => { cellRefs.current[c.cellId] = h; }}
                cellId={c.cellId}
                deviceId={deviceId}
                channel={c.channel}
                channelName={channelNameOf(c.channel)}
                date={date}
                rate={rate}
                focused={focusedCellId === c.cellId}
                showControls={mode === "independent"}
                onFocus={handleFocus}
                onFilesChange={handleFilesChange}
                onCursorChange={handleCursorChange}
              />
            ))}
          </div>
        </div>

        {/* Timeline de la camara enfocada */}
        <div className="flex-shrink-0">
          <div className="px-2 py-0.5 bg-panel border-t border-line text-[11px] text-muted">
            {focusedChannel != null
              ? `Barra de tiempo — CH${focusedChannel}${channelNameOf(focusedChannel) ? ` · ${channelNameOf(focusedChannel)}` : ""} (camara enfocada)${mode === "sync" ? " · clic = mover TODAS" : " · clic = mover esta"}`
              : "Selecciona (clic) una camara con canal para ver su barra de tiempo"}
          </div>
          <Timeline
            date={new Date(`${date}T00:00:00`)}
            files={focusedFiles}
            cursorIso={focusedCursor || undefined}
            onSeek={(iso) => seekTargets(iso)}
            selection={selection}
            onSelectionChange={setSelection}
            selectMode={selectMode}
            onSelectModeEnd={() => setSelectMode(false)}
          />
        </div>
      </section>

      {deviceId != null && focusedChannel != null && selection && (
        <DownloadRangeModal
          open={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          deviceId={deviceId}
          channel={focusedChannel}
          initialStart={selection.start}
          initialEnd={selection.end}
        />
      )}
    </div>
  );
}
